import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { fmtCupo } from '../utils/format';
import { Calendar, Plus, Trash2, Users, ChevronLeft, ChevronRight, X, Clock, MapPin, Video, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const toTitleCase = (s) => {
  if (!s) return '';
  return s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
};

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted}-${dv}`;
};

const CalendarioCapacitaciones = () => {
  const { token } = useAuth();
  const [sesiones, setSesiones] = useState([]);
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showDetalle, setShowDetalle] = useState(null);
  const [inscripciones, setInscripciones] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [form, setForm] = useState({ capacitacion_id: '', fecha: '', hora_inicio: '08:00', hora_fin: '17:00', cupo_maximo: 50, lugar: 'E-Learning', url_sesion: '', notas: '' });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSesiones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sesiones`, { headers });
      setSesiones(res.data);
    } catch { toast.error('Error al cargar sesiones'); }
  }, [token]);

  const fetchCapacitaciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/capacitaciones`, { headers });
      setCapacitaciones(res.data.filter(c => c.calendarizable));
    } catch { }
  }, [token]);

  useEffect(() => { fetchSesiones(); fetchCapacitaciones(); }, [fetchSesiones, fetchCapacitaciones]);

  const handleCreate = async () => {
    if (!form.capacitacion_id || !form.fecha) { toast.error('Seleccione curso y fecha'); return; }
    try {
      await axios.post(`${API}/sesiones`, form, { headers });
      toast.success('Sesion creada');
      setShowModal(false);
      setForm({ capacitacion_id: '', fecha: '', hora_inicio: '08:00', hora_fin: '17:00', cupo_maximo: 50, lugar: 'E-Learning', url_sesion: '', notas: '' });
      fetchSesiones();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    setConfirmAction({
      message: '¿Esta seguro de eliminar esta sesion? Esta accion no se puede deshacer.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/sesiones/${id}`, { headers });
          toast.success('Sesion eliminada');
          fetchSesiones();
          setShowDetalle(null);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmAction(null);
      }
    });
  };

  const handleCancelInscripcion = async (inscId) => {
    setConfirmAction({
      message: '¿Cancelar esta inscripcion? El cupo sera liberado.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/sesiones/inscripcion/${inscId}`, { headers });
          toast.success('Inscripcion cancelada');
          fetchSesiones();
          if (showDetalle) loadInscripciones(showDetalle.id);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmAction(null);
      }
    });
  };

  const loadInscripciones = async (sesionId) => {
    try {
      const res = await axios.get(`${API}/sesiones/${sesionId}/inscripciones`, { headers });
      setInscripciones(res.data);
    } catch { setInscripciones([]); }
  };

  const openDetalle = (sesion) => {
    setShowDetalle(sesion);
    loadInscripciones(sesion.id);
  };

  // Calendar rendering
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const getSesionesForDay = (day) => {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return sesiones.filter(s => s.fecha === dateStr);
  };

  return (
    <div className="space-y-6" data-testid="calendario-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-teal-600" />
          Calendario de Capacitaciones
        </h1>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          data-testid="create-session-btn">
          <Plus className="w-4 h-4" /> Nueva Sesion
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200">
          <button onClick={() => setCurrentMonth(new Date(year, month-1))} className="p-1 hover:bg-slate-200 rounded-lg">
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h2 className="text-lg font-semibold text-slate-800">{MESES[month]} {year}</h2>
          <button onClick={() => setCurrentMonth(new Date(year, month+1))} className="p-1 hover:bg-slate-200 rounded-lg">
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </div>
        <div className="grid grid-cols-7">
          {['Dom','Lun','Mar','Mie','Jue','Vie','Sab'].map(d => (
            <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-slate-500 border-b border-slate-100">{d}</div>
          ))}
          {days.map((day, idx) => {
            const daySesiones = day ? getSesionesForDay(day) : [];
            const isToday = day && new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
            return (
              <div key={idx} className={`min-h-[100px] border-b border-r border-slate-100 p-1 ${!day ? 'bg-slate-50/50' : 'hover:bg-slate-50/30'}`}>
                {day && (
                  <>
                    <div className={`text-xs font-medium mb-1 px-1 ${isToday ? 'bg-teal-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-slate-500'}`}>
                      {day}
                    </div>
                    {daySesiones.map(s => (
                      <div key={s.id} onClick={() => openDetalle(s)}
                        className={`text-[10px] px-1.5 py-1 mb-0.5 rounded cursor-pointer truncate font-medium ${
                          s.estado === 'cerrada' ? 'bg-red-100 text-red-700' : s.cupo_disponible <= 5 ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'
                        }`} title={`${s.capacitacion_nombre} - ${s.cupo_disponible}/${fmtCupo(s.cupo_maximo)} cupos`}>
                        {toTitleCase(s.capacitacion_nombre)}
                        <span className="ml-1 opacity-70">({s.inscritos}/{fmtCupo(s.cupo_maximo)})</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* List View */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Proximas Sesiones</h3>
        <div className="space-y-2">
          {sesiones.filter(s => s.fecha >= new Date().toISOString().slice(0,10)).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No hay sesiones programadas</p>
          )}
          {sesiones.filter(s => s.fecha >= new Date().toISOString().slice(0,10)).map(s => (
            <div key={s.id} onClick={() => openDetalle(s)}
              className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-200 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${s.estado === 'cerrada' ? 'bg-red-500' : s.cupo_disponible <= 5 ? 'bg-amber-500' : 'bg-teal-500'}`} />
                <div>
                  <p className="text-sm font-medium text-slate-800">{toTitleCase(s.capacitacion_nombre)}</p>
                  <p className="text-xs text-slate-500">{s.fecha.split('-').reverse().join('-')} | {s.hora_inicio} - {s.hora_fin} | {s.lugar}
                    {s.url_sesion && (
                      <a href={s.url_sesion} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="ml-2 text-teal-600 hover:text-teal-800 inline-flex items-center gap-0.5">
                        <Video className="w-3 h-3" /><ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-700">{s.inscritos}/{fmtCupo(s.cupo_maximo)}</p>
                  <p className="text-[10px] text-slate-400">{s.cupo_disponible} disponible(s)</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${s.estado === 'cerrada' ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                  {s.estado}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Nueva Sesion */}
      {showModal && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="create-session-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Nueva Sesion</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Curso</label>
                <select value={form.capacitacion_id} onChange={e => setForm({...form, capacitacion_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="session-course-select">
                  <option value="">Seleccionar curso</option>
                  {capacitaciones.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                <input type="date" value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="session-date" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hora Inicio</label>
                  <input type="time" value={form.hora_inicio} onChange={e => setForm({...form, hora_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hora Fin</label>
                  <input type="time" value={form.hora_fin} onChange={e => setForm({...form, hora_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cupo Maximo</label>
                  <input type="number" value={form.cupo_maximo} onChange={e => setForm({...form, cupo_maximo: parseInt(e.target.value) || 50})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="session-cupo" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lugar</label>
                  <input type="text" value={form.lugar} onChange={e => setForm({...form, lugar: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL Teams / Meet</label>
                <input type="url" value={form.url_sesion || ''} onChange={e => setForm({...form, url_sesion: e.target.value})}
                  placeholder="https://teams.microsoft.com/..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="session-url" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <textarea value={form.notas || ''} onChange={e => setForm({...form, notas: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={2} />
              </div>
              <button onClick={handleCreate}
                className="w-full py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm" data-testid="save-session-btn">
                Crear Sesion
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Detalle Sesion */}
      {showDetalle && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowDetalle(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="session-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{toTitleCase(showDetalle.capacitacion_nombre)}</h2>
                <p className="text-sm text-slate-500 flex items-center gap-3 mt-1">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{showDetalle.fecha.split('-').reverse().join('-')}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{showDetalle.hora_inicio} - {showDetalle.hora_fin}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{showDetalle.lugar}</span>
                  {showDetalle.url_sesion && (
                    <a href={showDetalle.url_sesion} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-teal-600 hover:text-teal-800 hover:underline">
                      <Video className="w-3.5 h-3.5" />Link<ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDelete(showDetalle.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setShowDetalle(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="px-6 py-3 bg-slate-50 flex items-center justify-between border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  {showDetalle.inscritos} / {fmtCupo(showDetalle.cupo_maximo)} inscritos
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${showDetalle.estado === 'cerrada' ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                  {showDetalle.cupo_disponible} cupos disponibles
                </span>
              </div>
            </div>
            <div className="overflow-auto flex-1 px-6 py-4">
              {inscripciones.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No hay inscripciones</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">RUT</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Nombre</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Cargo</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Empresa</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Estado</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inscripciones.map((insc, i) => (
                      <tr key={insc.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-3 py-2 text-slate-400">{i+1}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{formatRut(insc.rut_trabajador)}</td>
                        <td className="px-3 py-2 text-slate-800">{toTitleCase(insc.nombre_trabajador)}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{toTitleCase(insc.cargo)}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{toTitleCase(insc.empresa_nombre)}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-100 text-teal-700">{insc.estado}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => handleCancelInscripcion(insc.id)}
                            className="text-xs text-red-500 hover:underline">Cancelar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      , document.body)}
      {/* Modal Confirmacion */}
      {confirmAction && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-slate-700">{confirmAction.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="confirm-cancel-btn">Cancelar</button>
              <button onClick={confirmAction.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                data-testid="confirm-ok-btn">Confirmar</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default CalendarioCapacitaciones;
